/**
 * Aplicación del tema claro, oscuro o según el sistema (Req. 8.3).
 *
 * El tema elegido vive en los ajustes (IndexedDB), que se leen de forma
 * asíncrona. Para aplicarlo ANTES de pintar y evitar un destello del tema
 * equivocado, se guarda además una copia en localStorage que lee un script
 * pequeño en index.html. La copia es solo un atajo visual: si se pierde, la
 * app vuelve a aplicarlo al arrancar.
 */

import type { Tema } from '@/db/types';

/** Debe coincidir con la clave que lee el script de index.html. */
export const CLAVE_TEMA = 'duofit-tema';

/**
 * Color de la barra del sistema en cada tema: el fondo de Foco, para que la
 * barra de estado y la app se vean como una sola superficie. Deben coincidir
 * con --color-fondo de index.css y con las etiquetas theme-color de index.html.
 */
export const COLOR_BARRA = { claro: '#f1f4f0', oscuro: '#161c19' } as const;

const CONSULTA_OSCURO = '(prefers-color-scheme: dark)';

/** true si el sistema está en modo oscuro. */
export function sistemaEnOscuro(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.(CONSULTA_OSCURO).matches === true;
}

/** El tema que se ve: el elegido o, con "sistema", el del iPhone. */
export function temaEfectivo(tema: Tema, oscuroDelSistema: boolean): 'claro' | 'oscuro' {
  if (tema === 'claro' || tema === 'oscuro') return tema;
  return oscuroDelSistema ? 'oscuro' : 'claro';
}

/**
 * Las etiquetas theme-color de index.html siguen al sistema con `media`. Si se
 * elige un tema distinto al del sistema, se fijan las dos al color del tema
 * elegido; con "sistema" vuelven a seguirlo.
 */
function actualizarColorBarra(tema: Tema): void {
  const etiquetas = document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]');
  for (const etiqueta of etiquetas) {
    const esOscura = etiqueta.media.includes('dark');
    const color =
      tema === 'sistema' ? (esOscura ? COLOR_BARRA.oscuro : COLOR_BARRA.claro) : COLOR_BARRA[tema];
    etiqueta.setAttribute('content', color);
  }
}

export function aplicarTema(tema: Tema): void {
  if (typeof document === 'undefined') return;
  const raiz = document.documentElement;
  if (tema === 'sistema') {
    raiz.removeAttribute('data-tema');
  } else {
    raiz.setAttribute('data-tema', tema);
  }
  actualizarColorBarra(tema);
}

export function guardarTemaLocal(tema: Tema): void {
  try {
    localStorage.setItem(CLAVE_TEMA, tema);
  } catch {
    // Sin localStorage solo se pierde el atajo; el tema se aplica al arrancar.
  }
}

/** Aplica el tema y guarda el atajo para el siguiente arranque. */
export function establecerTema(tema: Tema): void {
  aplicarTema(tema);
  guardarTemaLocal(tema);
}

/** Avisa cuando el iPhone cambia entre claro y oscuro. Devuelve cómo dejar de escuchar. */
export function escucharSistema(alCambiar: (oscuro: boolean) => void): () => void {
  if (typeof window === 'undefined' || window.matchMedia === undefined) return () => {};
  const consulta = window.matchMedia(CONSULTA_OSCURO);
  const oyente = () => alCambiar(consulta.matches);
  consulta.addEventListener('change', oyente);
  return () => consulta.removeEventListener('change', oyente);
}
